import { useEffect, useState } from 'react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import form from '../../styles/form.module.css';

const empty = {
  code: '',
  discountType: 'FIXED',
  discountValue: '',
  minOrderAmount: '',
  maxRedemptionsGlobal: '',
  maxRedemptionsPerUser: '',
  startsAtDate: '',
  endsAtDate: '',
  status: 'ACTIVE',
  stackable: false,
};

export default function CouponFormDrawer({ open, onClose, onSave, coupon, saving, error }) {
  const [data, setData] = useState(empty);
  const isEdit = !!coupon;

  useEffect(() => {
    if (!open) return;
    setData(coupon ? {
      ...empty,
      ...coupon,
      startsAtDate: coupon.startsAt ? coupon.startsAt.slice(0, 10) : '',
      endsAtDate: coupon.endsAt ? coupon.endsAt.slice(0, 10) : '',
    } : empty);
  }, [coupon, open]);

  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...data,
      code: data.code.toUpperCase(),
      discountValue: Number(data.discountValue) || 0,
      minOrderAmount: Number(data.minOrderAmount) || 0,
      maxRedemptionsGlobal: data.maxRedemptionsGlobal === '' ? null : Number(data.maxRedemptionsGlobal),
      maxRedemptionsPerUser: data.maxRedemptionsPerUser === '' ? null : Number(data.maxRedemptionsPerUser),
      startsAt: data.startsAtDate ? `${data.startsAtDate}T00:00:00Z` : null,
      endsAt: data.endsAtDate ? `${data.endsAtDate}T23:59:59Z` : null,
    });
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit coupon' : 'Create coupon'}
      subtitle="Customers apply this code at checkout"
      width={440}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create coupon'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && <p className={form.hint} style={{ color: 'var(--color-error)', marginBottom: 14 }}>{error}</p>}

        <label className={form.field}>
          <span className={form.label}>Coupon code</span>
          <input
            className={form.input}
            required
            disabled={isEdit}
            value={data.code}
            onChange={set('code')}
            placeholder="WELCOME50"
            style={{ textTransform: 'uppercase' }}
          />
          {isEdit && <span className={form.hint}>Code can't be changed after creation.</span>}
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Discount type</span>
            <select className={form.select} value={data.discountType} onChange={set('discountType')}>
              <option value="FIXED">Flat amount (₹)</option>
              <option value="PERCENT">Percentage (%)</option>
            </select>
          </label>
          <label className={form.field}>
            <span className={form.label}>Value</span>
            <input className={form.input} type="number" required min="0" value={data.discountValue} onChange={set('discountValue')} />
          </label>
        </div>

        <label className={form.field}>
          <span className={form.label}>Minimum order</span>
          <div className={form.prefixInput}>
            <span>₹</span>
            <input className={form.input} type="number" min="0" value={data.minOrderAmount} onChange={set('minOrderAmount')} />
          </div>
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Total redemption limit</span>
            <input className={form.input} type="number" min="1" placeholder="Unlimited" value={data.maxRedemptionsGlobal} onChange={set('maxRedemptionsGlobal')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>Per-customer limit</span>
            <input className={form.input} type="number" min="1" placeholder="Unlimited" value={data.maxRedemptionsPerUser} onChange={set('maxRedemptionsPerUser')} />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Starts on</span>
            <input className={form.input} type="date" value={data.startsAtDate} onChange={set('startsAtDate')} />
          </label>
          <label className={form.field}>
            <span className={form.label}>Ends on</span>
            <input className={form.input} type="date" value={data.endsAtDate} onChange={set('endsAtDate')} />
          </label>
        </div>

        <label className={form.field}>
          <span className={form.label}>Status</span>
          <select className={form.select} value={data.status} onChange={set('status')}>
            <option value="ACTIVE">Active</option>
            <option value="PAUSED">Paused</option>
            <option value="INACTIVE">Inactive</option>
            <option value="EXPIRED">Expired</option>
          </select>
        </label>

        <div className={form.toggleRow}>
          <div>
            <span className={form.label}>Stackable</span>
            <p className={form.hint}>Allow combining with other active coupons.</p>
          </div>
          <button
            type="button"
            className={`${form.toggle} ${data.stackable ? form.toggleOn : ''}`}
            onClick={() => setData((d) => ({ ...d, stackable: !d.stackable }))}
            aria-label="Toggle stackable"
          />
        </div>
      </form>
    </Drawer>
  );
}
